import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';

function Notifications() {
  const { token, isAuthenticated } = useAuth();
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchNotifications();
  }, [isAuthenticated, navigate]);

  // Set page title for Notifications page
  useEffect(() => {
    setPageTitle('Notifications');
  }, [setPageTitle]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();
      setNotifications(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: 1 } : n)
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: 1 }))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete notification');
      }

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      setError(err.message);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'verification':
        return '✓';
      case 'follow_request':
        return '👤';
      case 'follow_accepted':
      case 'invite_accepted': // #358
        return '✓';
      case 'new_follower':
        return '👥';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'verification':
        return 'success';
      case 'follow_request':
        return 'primary';
      case 'follow_accepted':
      case 'invite_accepted': // #358
        return 'success';
      case 'new_follower':
        return 'info';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading notifications...</p>
      </Container>
    );
  }

  const unreadCount = notifications.filter(n => n.is_read === 0).length;

  return (
    <Container className="my-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          Notifications
          {unreadCount > 0 && (
            <Badge bg="danger" className="ms-2">{unreadCount}</Badge>
          )}
        </h2>
        {unreadCount > 0 && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleMarkAllAsRead}
          >
            Mark All as Read
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {notifications.length > 0 ? (
        <div>
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className="mb-3"
              style={{
                backgroundColor: notification.is_read === 0 ? '#f8f9fa' : 'white',
                borderLeft: notification.is_read === 0 ? '4px solid #0d6efd' : '1px solid #dee2e6'
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-2">
                      <Badge
                        bg={getNotificationColor(notification.notification_type)}
                        className="me-2"
                      >
                        {getNotificationIcon(notification.notification_type)} {notification.notification_type.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {notification.is_read === 0 && (
                        <Badge bg="danger" className="me-2">New</Badge>
                      )}
                      <small className="text-muted">
                        {new Date(notification.created_at).toLocaleString()}
                      </small>
                    </div>
                    <h5 className="mb-2">{notification.title}</h5>
                    <p className="mb-2">{notification.message}</p>
                    {notification.related_user_display_name && (
                      <div className="mt-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          onClick={() => navigate(`/profile/${notification.related_user_id}`)}
                        >
                          View {notification.related_user_display_name}'s Profile
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    {notification.is_read === 0 && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleMarkAsRead(notification.id)}
                      >
                        Mark as Read
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteNotification(notification.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-5">
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔔</div>
          <h4>No notifications yet</h4>
          <p className="text-muted">You'll see notifications here when you receive them</p>
        </div>
      )}
    </Container>
  );
}

export default Notifications;
